import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge, Button, Label, Media, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from 'reactstrap';
import Flex from '../../common/Flex';
import moment from 'moment';
import 'moment/locale/es';
import { faCalendarCheck, faHandPointUp, faMapMarkerAlt, faTrashAlt, faUndo, faUser } from '@fortawesome/free-solid-svg-icons';
import { getReservationDetails, setUpdateStatusReservation } from '../../../helpers/api/areas';
import handleErrorMessage from '../../../helpers/errors/handleErrorMessage';
import { PreloadState } from '../../UI/preloading/PreloadState';
import ComposeAttachment from '../../complain/ComposeAttachment';
import { useSelector } from 'react-redux';
import { SUCCESS_RESPONSE } from '../../../helpers/codeResponses/codes';
import { Toast } from './../../UI/toast/Toast';
import { checkUserToken } from '../../../helpers/api/auth';

moment.locale('es');

const getCircleStackIcon = (icon, transform) => (
    <span className="fa-stack ml-n1 mr-3">
        <FontAwesomeIcon icon="circle" className="text-200 fa-stack-2x" />
        <FontAwesomeIcon icon={icon} transform={transform ?? ''} className="text-primary fa-stack-1x" inverse />
    </span>
)

const EventModalMediaContent = ({ icon, heading, content, children }) => (
    <Media className="mt-3">
        {getCircleStackIcon(icon)}
        <Media body>
            <>
                <h6>{heading}</h6>
                {children || <p className="mb-0 text-justify">{content}</p>}
            </>
        </Media>
    </Media>
)

const CalendarEventModal = ({ isOpenModal, setIsOpenModal, modalEventContent, mutate }) => {

    const { token, role, property } = useSelector(state => state.auth.user)

    const toggle = () => setIsOpenModal(!isOpenModal);

    const { title, end, start } = isOpenModal && modalEventContent.event;
    const { id } = isOpenModal && modalEventContent.event.extendedProps;

    const [details, setDetails] = useState({ data: null, loading: true });
    const [loading, setLoading] = useState(false);
    const [isFromMe, setIsFromMe] = useState(false);

    const changeStatus = async (status) => {
        setLoading(true);
        try {
            const res = await setUpdateStatusReservation(id, status);
            if (res.code === SUCCESS_RESPONSE.OK) {
                Toast.fire({ type: 'success', title: `Has ${status === 'cancelled' ? 'cancelado' : 'aceptado'} con exito` });
                mutate();
                toggle();
            }
        } catch (error) {
            handleErrorMessage(error);
        } finally {
            setLoading(false);
        }

    }

    useEffect(() => {
        if (!isOpenModal) {
            setDetails({ data: null, loading: true });
            setIsFromMe(false);
        }
    }, [isOpenModal])

    useEffect(() => {
        let isMounted = true;
        if (id) {
            getReservationDetails(id)
                .then(({ data }) => isMounted && setDetails(prev => ({ ...prev, data })))
                .catch(error => handleErrorMessage(error))
                .finally(() => isMounted && setDetails(prev => ({ ...prev, loading: false })))
        }
        return () => {
            isMounted = false;
        }
    }, [id])

    useEffect(() => {
        let isMounted = true;
        if (details.data && role === 'property') {
            checkUserToken(token)
                .then((data) => {
                    if (data.username === details.data?.by?.user) isMounted && setIsFromMe(true);
                })
                .catch(error => handleErrorMessage(error));
        }
        return () => {
            isMounted = false;
        }
    }, [details, role, token])

    const closeBtn = (
        <button className="close font-weight-normal" onClick={toggle}>
            &times;
        </button>
    );

    const displayTitle = details?.data?.description || title;
    const displayStart = details?.data?.start_at || start;
    const displayEnd = details?.data?.end_at || end;

    return (
        <Modal isOpen={isOpenModal} toggle={toggle} modalClassName="theme-modal" contentClassName="border" centered>
            <ModalHeader toggle={toggle} tag="div" className="px-card bg-light border-0 flex-between-center" close={closeBtn}>
                <h5 className="mb-0 d-flex align-items-center">
                    {displayStart && moment(displayStart).format('MMMM, DD')}
                    {details?.data?.status === "processing" && <Badge className='ml-2' pill={true} color={'info'}>En proceso</Badge>}
                    {details?.data?.status === "accepted" && <Badge className='ml-2' pill={true} color={'success'}>Aceptado</Badge>}
                    {details?.data?.status === "cancelled" && <Badge className='ml-2' pill={true} color={'danger'}>Rechazado</Badge>}
                </h5>
            </ModalHeader>
            <ModalBody className="px-card pb-card pt-1 fs--1">
                <PreloadState loading={details.loading}>
                    <>
                        {details.data && <EventModalMediaContent icon={faUser} heading="Organizador">
                            <div className="mb-1" dangerouslySetInnerHTML={{ __html: `<b>${details.data.by.dni ? details.data.by.dni : 'DNI no registrado'}</b> ${details.data.by.firstname} ${details.data.by.lastname}` }} />
                        </EventModalMediaContent>}
                        {displayTitle && <EventModalMediaContent icon="align-left" heading="Descripción" content={displayTitle} />}
                        {(displayEnd || displayStart) && (
                            <EventModalMediaContent icon={faCalendarCheck} heading="Fecha y hora">
                                <span>
                                    {moment(displayStart).format('LLLL')}
                                </span>
                                {displayEnd && (
                                    <>
                                        {' '}
                                        – <br /> <span>{moment(displayEnd).format('LLLL')}</span>
                                    </>
                                )}
                            </EventModalMediaContent>
                        )}
                        {details.data && (
                            <EventModalMediaContent icon={faMapMarkerAlt} heading="Ubicación">
                                <div className="mb-1" dangerouslySetInnerHTML={{ __html: details.data.area.description }} />
                            </EventModalMediaContent>
                        )}
                        {details.data && (
                            <div className="bg-light px-card py-3">
                                <Flex column inline className='w-100'>
                                    <Label className='ml-2'>Archivos adjuntos</Label>
                                    {details.data.guest_list && (() => {
                                        const newFile = {
                                            id: moment().format('YYYYMMDDHmmss') + 1,
                                            name: details.data.guest_list.split('/').pop(),
                                            size: null,
                                            type: details.data.guest_list,
                                        };
                                        return (
                                            <div>
                                                <h6 className='ml-2'>Lista de invitados</h6>
                                                <ComposeAttachment
                                                    {...newFile}
                                                    key={newFile.id}
                                                    downloadPath={details.data.guest_list}
                                                    download={true}
                                                />
                                            </div>)
                                    }
                                    )()}
                                    {details.data.proof_rental_fee && (role === 'manager' || (role === 'property' && isFromMe)) && (() => {
                                        const newFile = {
                                            id: moment().format('YYYYMMDDHmmss') + 1,
                                            name: details.data.proof_rental_fee.split('/').pop(),
                                            size: null,
                                            type: details.data.proof_rental_fee,
                                        };
                                        return (
                                            <div className='w-100'>
                                                <h6 className='ml-2'>Prueba de pago de renta</h6>
                                                <ComposeAttachment
                                                    {...newFile}
                                                    classeName='d-block'
                                                    key={newFile.id}
                                                    downloadPath={details.data.proof_rental_fee}
                                                    download={true}
                                                />
                                            </div>)
                                    }
                                    )()}
                                    {details.data.proof_deposit_fee && (role === 'manager' || (role === 'property' && isFromMe)) && (() => {
                                        const newFile = {
                                            id: moment().format('YYYYMMDDHmmss') + 1,
                                            name: details.data.proof_deposit_fee.split('/').pop(),
                                            size: null,
                                            type: details.data.proof_deposit_fee,
                                        };
                                        return (
                                            <div className='w-100'>
                                                <h6 className='ml-2'>Prueba de pago de depósito</h6>
                                                <ComposeAttachment
                                                    {...newFile}
                                                    classeName='d-block'
                                                    key={newFile.id}
                                                    downloadPath={details.data.proof_deposit_fee}
                                                    download={true}
                                                />
                                            </div>)
                                    }
                                    )()}
                                </Flex>
                            </div>
                        )}
                    </>
                </PreloadState>
            </ModalBody>
            <ModalFooter tag={Flex} justify="end" className="bg-light px-card border-top-0">
                <Button onClick={toggle} color="falcon-default" size="sm">
                    <FontAwesomeIcon icon={faUndo} className="fs--2 mr-2" />
                    <span>Volver</span>
                </Button>
                {!loading && details?.data?.status === 'processing' && <>
                    {role === 'manager' &&
                        <Button onClick={e => changeStatus('accepted')} color="falcon-success" size="sm">
                            <span>Aprobar</span>
                            <FontAwesomeIcon icon={faHandPointUp} className="fs--2 ml-1" />
                        </Button>}
                    {(details?.data?.by.id === property || role === 'manager') &&
                        <Button onClick={e => changeStatus('cancelled')} color="falcon-danger" size="sm">
                            <span>Cancelar</span>
                            <FontAwesomeIcon icon={faTrashAlt} className="fs--2 ml-1" />
                        </Button>}
                </>}
                {loading && <Button color='falcon-default' disabled={loading} size='sm'>Actualizando estado... <Spinner size="sm" /></Button>}
            </ModalFooter>
        </Modal>
    );
}

export default CalendarEventModal;
